/**
 * Pure rules for the Hierarchy tab's "Show on the map" lenses: the lens list, the Renewal date
 * filter (presets + custom range), the Health rating filter, the Renewals lens highlight + summary,
 * and the active-filter count. Shared so the client and tests agree on one definition.
 */
import { DEFAULT_DEAL_FILTERS, type DealFilters } from './account-hierarchy-deal-model';
import { arrAtRiskOf, daysUntilRenewal, type WhitespaceStatus } from './account-hierarchy-whitespace';
import { fiscalRange, inFiscalRange } from './fiscal-period';

/**
 * Switchable lenses; the highlight is the intersection of the ones that are ON. Customer status is not
 * switchable: its colour is always painted, and its legend selection (`legendStatuses`) is the third lens.
 */
export const HIERARCHY_LENSES = ['deals', 'renewals'] as const;
export type HierarchyLensKey = (typeof HIERARCHY_LENSES)[number];
export type LensFlags = Record<HierarchyLensKey, boolean>;
export const DEFAULT_LENS_FLAGS: LensFlags = { deals: true, renewals: false };

/**
 * Highlighted ids = intersection of the ACTIVE lenses (`null` = lens off or no set). Returns null
 * when no lens produces a set, i.e. nothing is dimmed.
 */
export function intersectLensIds(lenses: ReadonlyArray<ReadonlySet<string> | null>): Set<string> | null {
  const active = lenses.filter((l): l is ReadonlySet<string> => l !== null);
  if (active.length === 0) return null;
  const [first, ...rest] = active;
  return new Set([...first].filter(id => rest.every(l => l.has(id))));
}

export const RENEWAL_PRESETS = [
  'any', 'next_30', 'next_60', 'next_90', 'past_due', 'this_fiscal_quarter', 'next_fiscal_quarter', 'custom',
] as const;
export type RenewalPreset = (typeof RENEWAL_PRESETS)[number];

export interface RenewalFilter {
  preset: RenewalPreset;
  /** Custom range bounds as `YYYY-MM-DD` (inclusive); either side may be blank (open-ended). */
  from: string | null;
  to: string | null;
}

export const DEFAULT_RENEWAL_FILTER: RenewalFilter = { preset: 'any', from: null, to: null };

const NEXT_DAYS: Partial<Record<RenewalPreset, number>> = { next_30: 30, next_60: 60, next_90: 90 };

/** A real calendar date as `YYYY-MM-DD` (rejects "2026-02-30" and free text). */
function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

export type RenewalRangeError = 'invalid_from' | 'invalid_to' | 'inverted';

/** Why a custom range cannot be applied (null = fine). A blank side is open-ended, not an error. */
export function renewalRangeError(from: string | null, to: string | null): RenewalRangeError | null {
  if (from !== null && !isIsoDate(from)) return 'invalid_from';
  if (to !== null && !isIsoDate(to)) return 'invalid_to';
  if (from !== null && to !== null && from > to) return 'inverted';
  return null;
}

/** "Any" (and a custom range with both ends blank, or one that cannot be applied) filters nothing. */
export function renewalFilterActive(filter: RenewalFilter): boolean {
  if (filter.preset === 'any') return false;
  if (filter.preset === 'custom') {
    return (filter.from !== null || filter.to !== null) && renewalRangeError(filter.from, filter.to) === null;
  }
  return true;
}

/** Does a renewal date (`YYYY-MM-DD`, or null = none on file) fall inside the filter? An inactive filter matches every date. */
export function renewalFilterMatches(date: string | null, filter: RenewalFilter, today: Date): boolean {
  if (!renewalFilterActive(filter)) return true;
  if (!date) return false;
  const days = daysUntilRenewal(date, today);
  if (!Number.isFinite(days)) return false;
  const nextDays = NEXT_DAYS[filter.preset];
  if (nextDays !== undefined) return days >= 0 && days <= nextDays;
  if (filter.preset === 'past_due') return days < 0;
  if (filter.preset === 'custom') return (filter.from === null || date >= filter.from) && (filter.to === null || date <= filter.to);
  if (filter.preset === 'this_fiscal_quarter' || filter.preset === 'next_fiscal_quarter') {
    const quarter = fiscalRange('this_quarter', today);
    if (!quarter) return false;
    const range = filter.preset === 'this_fiscal_quarter' ? quarter : { from: quarter.to + 1, to: quarter.to + 3 };
    return inFiscalRange(range, Number(date.slice(0, 4)), Number(date.slice(5, 7)));
  }
  return true;
}

export const HEALTH_KEYS = ['green', 'yellow', 'red', 'none'] as const;
export type HealthKey = (typeof HEALTH_KEYS)[number];

export function healthKeyOf(rating: string | null): HealthKey {
  const r = rating?.trim().toLowerCase();
  return r === 'green' || r === 'yellow' || r === 'red' ? r : 'none';
}

/** Accounts with any selected health rating; null while nothing is selected (the filter is off). */
export function healthFilterIds(
  nodes: ReadonlyArray<{ id: string; health_rating: string | null }>,
  keys: readonly HealthKey[],
): Set<string> | null {
  if (keys.length === 0) return null;
  return new Set(nodes.filter(n => keys.includes(healthKeyOf(n.health_rating))).map(n => n.id));
}

type RenewalLensNode = {
  id: string;
  enterprise_renewal_date: string | null;
  health_rating: string | null;
  contracted_arr: number | null;
  arr_active_subscriptions: number | null;
};

/**
 * Accounts the Renewals lens highlights: a renewal date on file that falls in the chosen window
 * ("Any" = every date), and, when health ratings are picked, one of those ratings. An account
 * with no renewal date never matches, whatever its health.
 */
export function renewalLensIds(
  nodes: ReadonlyArray<Pick<RenewalLensNode, 'id' | 'enterprise_renewal_date' | 'health_rating'>>,
  renewal: RenewalFilter,
  health: readonly HealthKey[],
  today: Date,
): Set<string> {
  return new Set(
    nodes
      .filter(n => n.enterprise_renewal_date !== null
        && renewalFilterMatches(n.enterprise_renewal_date, renewal, today)
        && (health.length === 0 || health.includes(healthKeyOf(n.health_rating))))
      .map(n => n.id),
  );
}

/** "N accounts renewing · $ARR at risk": the matching accounts and the ARR on the line at renewal. */
export function renewalLensSummary(
  nodes: ReadonlyArray<Pick<RenewalLensNode, 'id' | 'contracted_arr' | 'arr_active_subscriptions'>>,
  ids: ReadonlySet<string>,
): { accounts: number; arrAtRisk: number } {
  let accounts = 0;
  let arrAtRisk = 0;
  for (const n of nodes) {
    if (!ids.has(n.id)) continue;
    accounts += 1;
    arrAtRisk += arrAtRiskOf(n) ?? 0;
  }
  return { accounts, arrAtRisk };
}

export interface FilterState {
  dealFilters: DealFilters;
  renewalFilter: RenewalFilter;
  healthFilter: readonly HealthKey[];
  legendStatuses: readonly WhitespaceStatus[];
}

/** Filters of the Deals / Renewals lenses that are ON plus the legend selection (always live), set away from their defaults, counted once each (0 at the defaults: Open deals). */
export function activeFilterCount(on: LensFlags, state: FilterState): number {
  let count = 0;
  if (on.deals) {
    const d = state.dealFilters;
    const statusesChanged = [...d.statuses].sort().join() !== [...DEFAULT_DEAL_FILTERS.statuses].sort().join();
    count += [statusesChanged, d.dateRange !== 'all', d.pipelines !== 'all', d.dealTypes !== 'all'].filter(Boolean).length;
  }
  if (on.renewals) count += [renewalFilterActive(state.renewalFilter), state.healthFilter.length > 0].filter(Boolean).length;
  if (state.legendStatuses.length > 0) count += 1;
  return count;
}
